import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import logoUrl from "../../assets/contrack-logo.svg";

export interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface SidebarProps {
  workspaces: NavItem[];
  monitoring: NavItem[];
  currentPath: string;
  onNavigate: (path: string) => void;
  user: any;
  roleLabel: string;
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export default function Sidebar({
  workspaces, monitoring, currentPath, onNavigate, user, roleLabel, open, onClose, onLogout,
}: SidebarProps) {
  const { t, i18n } = useTranslation();

  const NavButton = ({ item }: { item: NavItem }) => {
    const Icon = item.icon;
    const active = currentPath === item.path;
    return (
      <button onClick={() => onNavigate(item.path)} className={`cx-nav ${active ? "cx-nav-active" : ""}`}>
        <Icon className={`w-[17px] h-[17px] shrink-0 ${active ? "text-night" : "text-faint"}`} />
        <span className="flex-1 text-left truncate">{item.label}</span>
        {item.badge && item.badge > 0 ? (
          <span className="min-w-[18px] h-[18px] bg-neg text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {item.badge > 99 ? "99+" : item.badge}
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <>
      {/* Backdrop (kun mobil) */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[248px] shrink-0 bg-panel border-r border-line flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="h-[56px] shrink-0 flex items-center px-4">
          <button onClick={() => onNavigate("/")} className="flex items-center gap-2">
            <img src={logoUrl} alt="Contrack" className="w-8 h-8 shrink-0" />
            <span className="text-[15px] font-bold text-night tracking-tight">Contrack AI</span>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          <p className="cx-section mt-1">{t("nav.section_workspaces")}</p>
          <div className="space-y-0.5 mb-5">
            {workspaces.map(item => <NavButton key={item.path} item={item} />)}
          </div>
          <p className="cx-section">{t("nav.section_monitoring")}</p>
          <div className="space-y-0.5">
            {monitoring.map(item => <NavButton key={item.path} item={item} />)}
          </div>
        </nav>

        {/* Bunn: språk + bruker */}
        <div className="px-3 pb-3 shrink-0">
          <div className="flex items-center justify-between py-2 px-2.5">
            <span className="text-[12px] font-medium text-muted">{t("lang.toggle_label")}</span>
            <div className="flex bg-black/[0.05] rounded-[8px] p-0.5">
              <button
                onClick={() => { i18n.changeLanguage("nb"); localStorage.setItem("contrack_lang", "nb"); }}
                className={`px-2.5 py-1 rounded-[6px] text-[11px] font-bold transition-all ${i18n.language?.startsWith("nb") ? "bg-white text-night shadow-sm" : "text-faint"}`}
              >NO</button>
              <button
                onClick={() => { i18n.changeLanguage("en"); localStorage.setItem("contrack_lang", "en"); }}
                className={`px-2.5 py-1 rounded-[6px] text-[11px] font-bold transition-all ${i18n.language?.startsWith("en") ? "bg-white text-night shadow-sm" : "text-faint"}`}
              >EN</button>
            </div>
          </div>
          <div className="mt-1 pt-2 border-t border-line flex items-center gap-2.5 px-2.5 py-1.5">
            <div className="w-8 h-8 rounded-[9px] bg-night/[0.06] flex items-center justify-center shrink-0">
              <span className="text-[11px] font-bold text-night">
                {user?.full_name?.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12.5px] font-semibold text-night truncate">{user?.full_name}</p>
              <p className="text-[11px] text-muted truncate">{roleLabel || user?.role}</p>
            </div>
            <button onClick={onLogout} className="p-1.5 rounded-lg text-faint hover:text-neg hover:bg-neg/10 transition-colors" title={t("nav.logg_ut")}>
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
